import { useState } from 'react';
import {
  confirmPasswordReset,
  requestPasswordReset,
  signIn,
  type Audience,
  type Session,
  type SignInOutcome,
} from '../auth';
import type { PortalConfig } from '../config';
import { beginSso } from '../sso';

// Sign-in for both audiences.
//
// The audience is picked before the credentials are sent, because it selects the
// Cognito pool. It is not a privilege choice -- picking "Staff" without a staff
// account just fails -- so it is presented as "which kind of account is this"
// rather than as anything the user could gain by getting wrong.
export function LoginPage({
  onSignedIn,
  config,
  ssoError,
}: {
  onSignedIn: (session: Session) => void;
  // Absent only if the config fetch itself failed, in which case no sign-in of
  // any kind can be attempted.
  config?: PortalConfig;
  // A federated sign-in that came back refused. Reported here rather than as a
  // fatal error because the password form below is still a way in.
  ssoError?: string;
}) {
  // A failed SSO attempt returns to this form, so it opens on the tab the person
  // was already using rather than making them find it again.
  const [audience, setAudience] = useState<Audience>(ssoError ? 'admin' : 'partner');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [outcome, setOutcome] = useState<SignInOutcome>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [resetting, setResetting] = useState(false);

  // Locals rather than reading through `config` at the point of use: narrowing a
  // prop does not survive into the click handler's closure, but a const's does.
  const sso = config?.adminSso;
  const clientId = config?.adminClientId;

  const run = async (action: () => Promise<SignInOutcome>) => {
    setBusy(true);
    setError(undefined);
    try {
      const next = await action();
      if (next.kind === 'signed-in') {
        onSignedIn(next.session);
      } else {
        setOutcome(next);
      }
    } catch (err) {
      setError(messageFor(err));
    } finally {
      setBusy(false);
    }
  };

  if (resetting) {
    return (
      <ResetPassword
        audience={audience}
        onCancel={() => setResetting(false)}
        onDone={() => setResetting(false)}
      />
    );
  }

  if (outcome?.kind === 'new-password') {
    return (
      <Challenge
        title="Choose a new password"
        hint="This account was created with a temporary password, which has to be replaced before you can sign in."
        label="New password"
        type="password"
        busy={busy}
        error={error}
        onSubmit={(value) => run(() => outcome.submit(value))}
      />
    );
  }

  if (outcome?.kind === 'totp') {
    return (
      <Challenge
        title="Two-factor code"
        hint="Enter the current 6-digit code from your authenticator app."
        label="Code"
        type="text"
        busy={busy}
        error={error}
        onSubmit={(value) => run(() => outcome.submit(value))}
      />
    );
  }

  if (outcome?.kind === 'totp-setup') {
    return (
      <main className="centered">
        <form
          className="card"
          onSubmit={(event) => {
            event.preventDefault();
            const code = new FormData(event.currentTarget).get('code');
            void run(() => outcome.submit(String(code ?? '')));
          }}
        >
          <h1>Set up two-factor authentication</h1>
          <p>
            Staff accounts require an authenticator app. Add this secret to yours, then enter the
            code it shows.
          </p>
          <label>
            Secret
            <input readOnly value={outcome.secret} onFocus={(e) => e.currentTarget.select()} />
          </label>
          <p className="hint">
            Or paste this into an app that accepts setup links:
            <br />
            <code className="wrap">{outcome.otpauthUri}</code>
          </p>
          <label>
            Code from the app
            <input name="code" inputMode="numeric" autoComplete="one-time-code" required />
          </label>
          {error && <p className="error">{error}</p>}
          <button type="submit" disabled={busy}>
            {busy ? 'Verifying…' : 'Finish setup'}
          </button>
        </form>
      </main>
    );
  }

  return (
    <main className="centered">
      <form
        className="card"
        onSubmit={(event) => {
          event.preventDefault();
          void run(() => signIn(audience, username.trim(), password));
        }}
      >
        <h1>SFTP portal</h1>
        <div className="segmented">
          <button
            type="button"
            className={audience === 'partner' ? 'active' : undefined}
            onClick={() => setAudience('partner')}
          >
            SFTP user
          </button>
          <button
            type="button"
            className={audience === 'admin' ? 'active' : undefined}
            onClick={() => setAudience('admin')}
          >
            Staff
          </button>
        </div>
        {audience === 'admin' && sso && clientId && (
          <>
            {ssoError && <p className="error">{ssoError}</p>}
            <button
              type="button"
              className="sso"
              disabled={busy}
              onClick={() => {
                setBusy(true);
                setError(undefined);
                // Leaves the page on success, so `busy` is deliberately never
                // cleared on the way out -- there is nothing left to re-enable.
                void beginSso(sso, clientId).catch((err) => {
                  setError(messageFor(err));
                  setBusy(false);
                });
              }}
            >
              Sign in with your work account
            </button>
            {/* Kept visible rather than hidden behind a link: while password
                sign-in is still enabled, it is the way back in if the identity
                provider is the thing that is broken. */}
            <p className="or">or sign in with a portal password</p>
          </>
        )}
        <label>
          {audience === 'admin' ? 'Email' : 'Username'}
          <input
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            autoComplete="username"
            required
          />
        </label>
        <label>
          Password
          <input
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="current-password"
            required
          />
        </label>
        {error && <p className="error">{error}</p>}
        <button type="submit" disabled={busy}>
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
        <button type="button" className="link" onClick={() => setResetting(true)}>
          Forgotten password?
        </button>
      </form>
    </main>
  );
}

function Challenge({
  title,
  hint,
  label,
  type,
  busy,
  error,
  onSubmit,
}: {
  title: string;
  hint: string;
  label: string;
  type: 'text' | 'password';
  busy: boolean;
  error?: string;
  onSubmit: (value: string) => void;
}) {
  const [value, setValue] = useState('');
  return (
    <main className="centered">
      <form
        className="card"
        onSubmit={(event) => {
          event.preventDefault();
          onSubmit(value);
        }}
      >
        <h1>{title}</h1>
        <p>{hint}</p>
        <label>
          {label}
          <input
            type={type}
            value={value}
            onChange={(event) => setValue(event.target.value)}
            autoComplete={type === 'password' ? 'new-password' : 'one-time-code'}
            required
          />
        </label>
        {error && <p className="error">{error}</p>}
        <button type="submit" disabled={busy}>
          {busy ? 'Working…' : 'Continue'}
        </button>
      </form>
    </main>
  );
}

function ResetPassword({
  audience,
  onCancel,
  onDone,
}: {
  audience: Audience;
  onCancel: () => void;
  onDone: () => void;
}) {
  const [username, setUsername] = useState('');
  const [sent, setSent] = useState(false);
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  const submit = async () => {
    setBusy(true);
    setError(undefined);
    try {
      if (!sent) {
        await requestPasswordReset(audience, username.trim());
        setSent(true);
      } else {
        await confirmPasswordReset(audience, username.trim(), code.trim(), password);
        onDone();
      }
    } catch (err) {
      setError(messageFor(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="centered">
      <form
        className="card"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <h1>Reset your password</h1>
        {!sent ? (
          <>
            <p>We will email a verification code to the address on your account.</p>
            <label>
              {audience === 'admin' ? 'Email' : 'Username'}
              <input value={username} onChange={(e) => setUsername(e.target.value)} required />
            </label>
          </>
        ) : (
          <>
            <p>Enter the code from the email and choose a new password.</p>
            <label>
              Code
              <input value={code} onChange={(e) => setCode(e.target.value)} required />
            </label>
            <label>
              New password
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                required
              />
            </label>
          </>
        )}
        {error && <p className="error">{error}</p>}
        <button type="submit" disabled={busy}>
          {busy ? 'Working…' : sent ? 'Set new password' : 'Send code'}
        </button>
        <button type="button" className="link" onClick={onCancel}>
          Back to sign in
        </button>
      </form>
    </main>
  );
}

// Cognito's errors are mostly readable, but a few are jargon that would send
// someone to support for no reason.
const messageFor = (err: unknown): string => {
  const raw = err instanceof Error ? err.message : String(err);
  if (/incorrect username or password|NotAuthorized/i.test(raw)) {
    return 'That username and password did not match.';
  }
  if (/User does not exist/i.test(raw)) return 'That username and password did not match.';
  if (/Password attempts exceeded/i.test(raw)) {
    return 'Too many attempts. Wait a few minutes before trying again.';
  }
  if (/Invalid code|CodeMismatch/i.test(raw)) return 'That code was not accepted. Try the next one.';
  return raw;
};
